import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import {
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Select,
  Typography,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { MediaManager } from '@/components';
import InputText from '@/components/InputText';
import { useAccessControl } from '@/features/access-control';
import useSnackbar from '@/hooks/useSnackbar';
import {
  useAddOfferMutation,
  useUpdateOfferMutation,
} from '@/features/organizations/api/contentApi';
import { ContentStatus, NewOffer, Offer } from '@/types';
import Frame from '@/components/Frame';

interface Props {
  open: boolean;
  selectedContent?: Offer;
  onClose: () => void;
}

const OfferSchema = Yup.object().shape({
  title: Yup.string()
    .strict(true)
    .min(4, 'Muy Corto!')
    .max(50, 'Muy Largo!')
    .required('Es necesario tener un título de contenido'),
  images: Yup.array().of(Yup.string()).min(1, 'Debes añadir una imagen'),
  status: Yup.string()
    .strict(true)
    .required('Debes añadir un estado para este contenido'),
});

const OfferDrawer = ({ open, selectedContent, onClose }: Props) => {
  const { hasAccess } = useAccessControl();
  const { organizationId } = useParams();
  const [openSnackbar] = useSnackbar();
  const [addOffer, addOfferMutation] = useAddOfferMutation();
  const [updateOffer, updateOfferMutation] = useUpdateOfferMutation();

  const isNewContent = useMemo(() => !selectedContent?.id, [selectedContent]);

  const handleSubmit = useCallback(
    async (values: Offer | NewOffer) => {
      try {
        const isNewContent = !values.id;

        if (isNewContent) {
          const response = await addOffer({
            draft: values as NewOffer,
            organizationId: organizationId ?? '',
          });

          if ('error' in response) {
            openSnackbar('Error al crear el contenido oferta', 'error');
          } else {
            openSnackbar('Contenido oferta creado', 'success');
          }
        } else {
          const response = await updateOffer({
            content: values as Offer,
            contentId: selectedContent?.id ?? '',
            organizationId: organizationId ?? '',
          });

          if ('error' in response) {
            openSnackbar('Error al actualizar el contenido oferta', 'error');
          } else {
            openSnackbar('Contenido de oferta Actualizado', 'success');
          }
        }
      } catch (error) {
        console.error(error);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationId, selectedContent?.id]
  );

  return (
    <Drawer open={open} onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={{
          id: selectedContent?.id ?? '',
          title: selectedContent?.title ?? '',
          images: selectedContent?.images ?? [],
          status: selectedContent?.status ?? '',
          region: selectedContent?.region ?? '',
          component: selectedContent?.component ?? '',
        }}
        onSubmit={handleSubmit}
        validationSchema={OfferSchema}
      >
        {({ values, errors, setFieldValue, handleChange, isSubmitting }) => {
          return (
            <Form>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                padding={2}
              >
                <Typography variant="h2">
                  {isNewContent ? 'Crear' : 'Editar'} Oferta
                </Typography>
                <Paper
                  sx={{
                    borderRadius: '50%',
                  }}
                >
                  <IconButton color="primary">
                    <HugeiconsIcon icon={Cancel01Icon} />
                  </IconButton>
                </Paper>
              </Stack>

              <Stack gap={2} paddingX={2}>
                <Paper sx={{ padding: 2 }}>
                  <Typography variant="h4">Detalles</Typography>
                  <InputText
                    label="Nombre"
                    margin="normal"
                    value={values?.title ?? ''}
                    name="title"
                    size="small"
                  />
                </Paper>
                
                <Paper sx={{ padding: 2 }}>
                  <Stack spacing={3}>
                    <Typography variant="h4">Visualización</Typography>
                    <Frame>
                      <MediaManager
                        value={values?.images?.[0] ?? []}
                        onChange={(value: Offer['images']) => {
                          setFieldValue('images', value);
                        }}
                        helperText={errors?.images as string | undefined}
                      />
                    </Frame>
                    <FormControl fullWidth>
                      <InputLabel id="display-component">Component</InputLabel>
                      <Select
                        labelId="display-component"
                        value={values.component}
                        label="Componente"
                        name="component"
                        size="small"
                        onChange={(e) => {
                          setFieldValue('component', e.target.value);
                        }}
                      >
                        <MenuItem value="image">Imagen</MenuItem>
                        <MenuItem value="popup">Ventana flotanje</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Paper>

                <Paper sx={{ padding: 2 }} elevation={2}>
                  <Stack gap={2}>
                    <Typography variant="h4">Región</Typography>
                    <Stack gap={2}>
                      <Select
                        value={values?.region ?? ''}
                        size="small"
                        onChange={handleChange}
                        name="region"
                      >
                        <ListSubheader>Menú</ListSubheader>
                        <MenuItem value="header">Cabecera</MenuItem>
                        <MenuItem value="footer">Pie de Página</MenuItem>
                        <ListSubheader>Sitio web</ListSubheader>
                        <MenuItem value="website-popup">Ventana Flotante</MenuItem>
                      </Select>
                    </Stack>
                  </Stack>
                </Paper>

                <Paper sx={{ padding: 2 }} elevation={2}>
                  <Stack gap={2}>
                    <Typography variant="h4">Estado</Typography>
                    <Stack>
                      <FormControlLabel
                        label="Borrador"
                        labelPlacement="start"
                        sx={{
                          margin: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        control={
                          <Switch
                            name="status.draft"
                            value={'draft'}
                            checked={Boolean(
                              values?.status === ContentStatus.DRAFT
                            )}
                            onChange={(e) => {
                              handleChange(e);
                              if (e.target.checked) {
                                setFieldValue('status', 'draft');
                              }
                            }}
                          />
                        }
                      />
                      <FormControlLabel
                        label="Publicado"
                        labelPlacement="start"
                        sx={{
                          margin: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        control={
                          <Switch
                            name="status.published"
                            value={'published'}
                            checked={Boolean(
                              values?.status === ContentStatus.PUBLISHED
                            )}
                            onChange={(e) => {
                              handleChange(e);
                              if (e.target.checked) {
                                setFieldValue('status', 'published');
                              }
                            }}
                          />
                        }
                      />
                      <FormControlLabel
                        label="Archivado"
                        labelPlacement="start"
                        sx={{
                          margin: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        control={
                          <Switch
                            name="status.archived"
                            value={'archived'}
                            checked={Boolean(
                              values?.status === ContentStatus.ARCHIVED
                            )}
                            onChange={(e) => {
                              handleChange(e);
                              if (e.target.checked) {
                                setFieldValue('status', 'archived');
                              }
                            }}
                          />
                        }
                      />
                    </Stack>
                  </Stack>
                </Paper>

                <Stack direction="row" alignItems="center" gap={2}>
                  {hasAccess('create.content') ? (
                    <LoadingButton
                      variant="contained"
                      color="success"
                      type="submit"
                      loading={
                        addOfferMutation.isLoading ||
                        updateOfferMutation.isLoading
                      }
                    >
                      {isNewContent ? 'Crear' : 'Guardar'}
                    </LoadingButton>
                  ) : null}
                  <Button
                    disabled={isSubmitting}
                    variant="text"
                    onClick={onClose}
                  >
                    Cancelar
                  </Button>
                </Stack>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
};

export default OfferDrawer;
